import { useEffect, useState } from 'react';
import { api, type Anmelder } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage }
  from '../teile/Formular.tsx';

/**
 * Eine Abruf- oder BAFA-Datei einlesen, aus dem Posteingang heraus.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „was sich ändert oder frühere läufe an änderungen brauchen wir nicht. wir
 *    brauchen ein solides import aller dateien."
 *
 * Deshalb ist der Import kein eigener Bildschirm mehr und kein zweiter Weg für
 * Dateien. Er ist der zweite Schritt für genau die Zeilen im Posteingang, die
 * eine Tabelle sind. Die Änderungsliste Feld für Feld und das Laufprotokoll
 * stehen nicht mehr da.
 *
 * WAS AUFFÄLLT, BLEIBT ABER STEHEN. Doppelte Anschriften, nicht gelesene
 * Zeilen und Aufträge, die in der Datei fehlen: das sind keine Änderungen, das
 * sind Warnungen. Ein Import, der 365 Aufträge anfasst und dabei still ist,
 * ist der gefährlichere.
 */

const ART_TEXT: Record<string, string> = {
  order: 'Abruf',
  bafa: 'BAFA-Bericht',
  kunden: 'Kundenliste',
  unbekannt: 'nicht erkannt',
};

/** Wie die vier Kundenfelder heissen, wenn ein Mensch sie liest. */
const FELD_TEXT: Record<string, string> = {
  kunde_name: 'Kundenname',
  kunde_telefon: 'Telefon',
  kunde_mobil: 'Mobil',
  kunde_email: 'Mailadresse',
};

/**
 * Wie die Telekom-Felder heissen, wenn ein Mensch sie liest.
 *
 * `wfmt_kommentar` ist kein Wort, das jemand kennt. Wer vor der Frage steht,
 * ob er 274 Aufträge ändern lässt, darf nicht erst raten müssen, was ihm da
 * geändert wird.
 */
const TELEKOM_TEXT: Record<string, string> = {
  bafa_id: 'BAFA-ID',
  sm_nummer: 'SM-Nummer',
  kls_id: 'KLS-ID',
  bauauftrag_id: 'Bauauftrag-ID',
  belegnummer: 'Belegnummer',
  nvt: 'NVT-Gebiet',
  asb: 'AsB',
  onkz: 'ONKz',
  kunde_name: 'Kundenname',
  auftragstyp_tk: 'Auftragstyp (Telekom)',
  status_tk: 'Status (Telekom)',
  wfmt_kommentar: 'Bemerkung aus dem Vorsystem',
};

/**
 * Gibt es an dieser Vorschau etwas zu ENTSCHEIDEN?
 *
 * Die Liste steht hier und nur hier. Eine zweite Fassung im Server liefe beim
 * ersten Nachtrag auseinander, und dann übersprünge die Oberfläche eine
 * Übersicht, in der ein Befund steht.
 *
 * WAS HIER NICHT DRINSTEHT, IST ABSICHT: die Zahlen „neu", „geändert" und
 * „unverändert" sind das ERGEBNIS und keine Frage. Sie stehen hinterher in
 * dem einen Satz, den jeder liest.
 */
function zuEntscheiden(v: any): boolean {
  const d = v?.dateien ?? [];
  return (v?.unbrauchbar ?? []).length > 0
      || (v?.doppelteArt ?? []).length > 0
      || !!v?.kundenHinweis
      || !!v?.plan?.verdacht
      /* KENNUNGSKONFLIKTE UND AUSGESCHLOSSENE ORTE SIND AUSKUNFT, KEINE
         FRAGE (.524): sie stehen in jeder Order.csv wieder, und die Vorschau
         fragte jedes Mal dasselbe. Marvin, 28.10.2026: „Ich muss das dann
         nicht nochmal eintragen und ändern lassen." */
      || (v?.plan?.verworfen ?? []).length > 0
      || d.some((x: any) => x.art === 'unbekannt'
                         || (x.fehlendeSpalten ?? []).length > 0
                         || (x.spalten_gelesen_fehlt ?? []).length > 0);
}

export default function ImportVorschau({
  ich, eingangIds, zu, fertig,
}: {
  ich: Anmelder; eingangIds: string[]; zu: () => void; fertig: () => void;
}) {
  const [vorschau, setVorschau] = useState<any>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(true);

  const schluessel = eingangIds.join(',');
  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const v = await api.importVorschauAusEingang(eingangIds);
        if (weg) return;
        /* WENN NICHTS ZU ENTSCHEIDEN IST, WIRD NICHT GEFRAGT.
           -----------------------------------------------------------------
           ORIGINALTEXT MARVIN (08.09.2026)
             „diese übersicht brauchen wir nicht"

           Er hat auf „Einlesen" geklickt. Eine Übersicht danach beantwortet
           eine Frage, die er mit dem Klick schon beantwortet hat - und beim
           zwanzigsten Mal klickt er sie durch, ohne sie zu lesen. Genau das
           war am 27.08.2026 die Beanstandung am Hochladen („ich muss hier so
           oft auf einlesen klicken"), und die Antwort ist dieselbe.

           **DER RIEGEL BLEIBT, ER WIRD NUR NICHT MEHR JEDES MAL VORGELEGT.**
           Gibt es etwas zu sehen - eine unbrauchbare Datei, einen Verdacht,
           fehlende Spalten, einen Kennungskonflikt, ausgefilterte Orte, zwei
           Dateien derselben Art oder eine Kundenliste neben einem Abruf -,
           dann steht die Übersicht da wie bisher. Das ist die Regel des
           Hauses seit .77: der Riegel ersetzt den Menschen nicht, er HOLT
           ihn - und nur dann. */
        if (!zuEntscheiden(v) && ich.rechte.includes('import.anwenden')) {
          setErgebnis(await api.importAnwendenAusEingang(eingangIds));
        } else setVorschau(v);
      } catch (f) { if (!weg) setFehler(ausFehler(f)); }
      finally { if (!weg) setLaeuft(false); }
    })();
    return () => { weg = true; };
  }, [schluessel]);

  async function anwenden() {
    setLaeuft(true); setFehler(null);
    try {
      setErgebnis(await api.importAnwendenAusEingang(eingangIds));
      setVorschau(null);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const plan = vorschau?.plan;
  const kunden = vorschau?.kunden;
  const darfAnwenden = ich.rechte.includes('import.anwenden');
  // Der Abrufteil des Plans ist immer da, auch bei einer reinen Kundenliste:
  // dann steht ueberall eine Null. Die Kacheln zeigen ihn deshalb nur, wenn
  // wirklich eine Telekom-Tabelle dabei war - vier Nullen ueber einer
  // Kundenliste lesen sich wie „nichts passiert".
  const hatTelekom = (vorschau?.dateien ?? [])
    .some((d: any) => d.art === 'order' || d.art === 'bafa');

  return (
    /* DER TITEL SAGT, WAS WIRKLICH DALIEGT. „Aus der Telekom-Datei einlesen"
       über einer hochgeladenen Kundenliste ist keine Kleinigkeit: es ist die
       Überschrift, an der man vor dem Klicken prüft, ob man die richtige Datei
       erwischt hat. */
    <Dialog titel={!vorschau || hatTelekom
                     ? 'Aus der Telekom-Datei einlesen'
                     : 'Kundenliste einlesen'}
            laeuft={laeuft}
            zu={ergebnis ? fertig : zu} breite={820}>
      <Fehlerkasten lage={fehler} />

      {laeuft && !vorschau && !ergebnis && (
        <p className="klein leise">Die Datei wird gelesen.</p>
      )}

      {/* EINE DATEI, DIE NICHT MITLÄUFT, STEHT GANZ OBEN.
          -------------------------------------------------------------------
          ORIGINALTEXT MARVIN (08.09.2026)
            „ich habe beide dateien hochgeladen, jedoch hat das system nicht
             die KLS IDs eingetragen."

          Sein Abruf trug acht von einunddreissig Spalten - ohne Straße, ohne
          Ort, ohne KLS-ID. Die Datei brachte keine einzige Zeile mit, und
          daneben standen die 337 Änderungen der BAFA: **der Lauf sah
          vollständig aus.** Deshalb steht das hier über allem und nicht als
          rote Zeile in einer Tabellenspalte. */}
      {((vorschau?.unbrauchbar ?? ergebnis?.unbrauchbar ?? []) as any[])
        .map((u: any) => (
        <div key={u.datei} className="hinweis warnung" style={{ marginTop: 12 }}>
          <b>„{u.datei}" wurde nicht gelesen.</b>{' '}
          In der Datei fehlen: {u.fehlt.join(', ')}.
          {/* DER GRUND KOMMT VOM SERVER UND WIRD HIER NICHT NACHGEBAUT.
              Er steht in `regeln/import.ts` (`pflichtGrund`) und sagt, WAS an
              der fehlenden Spalte hängt - bei der Anschrift die Naht zwischen
              Abruf und BAFA, bei der Kennung die Wiedererkennung. Eine zweite
              Fassung hier liefe beim ersten Nachtrag auseinander, und dann
              stünde auf dem Bildschirm ein anderer Grund als in der Antwort,
              nach der jemand sucht. */}
          {u.grund && <> {u.grund}</>}
          {' '}{u.zeilen === 1 ? 'Die Zeile bringt' :
          `Alle ${u.zeilen} Zeilen bringen`} deshalb nichts mit.
          {' '}Das kommt vom Export im Portal: dort werden die Spalten
          mitgewählt. Die übrigen Dateien laufen weiter.
        </div>
      ))}

      {/* ----------------------------------------------------- Was drin ist */}
      {vorschau && (
        <table className="liste">
          <thead>
            <tr>
              <th>Datei</th>
              <th style={{ width: 150 }}>Erkannt als</th>
              <th className="zahl" style={{ width: 90 }}>Zeilen</th>
              {/* Bei einer Kundenliste zählt nicht die Anschrift, sondern
                  die brauchbare Zeile: eine ohne Zuordnung oder ohne einen
                  einzigen Wert fällt weg. */}
              <th className="zahl" style={{ width: 130 }}>
                {hatTelekom ? 'Mit Anschrift' : 'Brauchbar'}
              </th>
            </tr>
          </thead>
          <tbody>
            {vorschau.dateien.map((d: any) => (
              <tr key={d.name} style={{ cursor: 'default' }}>
                <td>{d.name}</td>
                <td>
                  <span className={'marke-zustand '
                    + (d.art === 'unbekannt' ? 'z-ueberfaellig' : 'z-laufend')}>
                    {ART_TEXT[d.art]}
                  </span>
                  {d.fehlendeSpalten.length > 0 && (
                    <div className="klein" style={{ color: 'var(--ueberfaellig)' }}>
                      Es fehlt: {d.fehlendeSpalten.join(', ')}
                    </div>
                  )}
                  {/* EINE FEHLENDE SPALTE UND EINE LEERE ZELLE SEHEN GLEICH
                      AUS - und das ist der teuerste Zustand, den es hier gibt.
                      Wird „SM-Auftrag T-COM" im Portal umbenannt, geht die
                      Datei durch, die SM-Nummer bleibt leer, ein leerer Wert
                      überschreibt nie, und die Vorschau sagt „0 werden
                      geändert". **Das liest sich wie „alles schon aktuell"
                      und heisst „diese Angabe wird nie wieder gepflegt".** */}
                  {(d.spalten_gelesen_fehlt ?? []).length > 0 && (
                    <div className="klein" style={{ color: 'var(--neu)' }}>
                      <b>Diese Spalten werden gelesen und stehen nicht in der
                      Datei:</b> {d.spalten_gelesen_fehlt.join(', ')}. Die
                      betroffenen Angaben bleiben leer und werden bei keinem
                      Auftrag mehr gepflegt - ein leerer Wert überschreibt
                      nie, deshalb sieht das unten wie „nichts geändert" aus.
                    </div>
                  )}
                  {/* WAS DIE TELEKOM NEU LIEFERT, WILL MAN WISSEN. Kein
                      Fehler, eine Auskunft - deshalb leise und zugeklappt. */}
                  {(d.spalten_unbekannt ?? []).length > 0 && (
                    <details className="klein leise">
                      <summary>
                        {d.spalten_unbekannt.length} Spalten in der Datei
                        werden nicht gelesen
                      </summary>
                      {d.spalten_unbekannt.join(', ')}
                    </details>
                  )}
                </td>
                <td className="zahl">{d.zeilen}</td>
                <td className="zahl">{d.gelesen}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* EIN ORT, IN DEM WIR NICHT ARBEITEN, KOMMT GAR NICHT ERST HEREIN.
          -------------------------------------------------------------------
          ORIGINALTEXT MARVIN (01.09.2026)
            „überall stehen die orte noch drin, die wir doch komplett
             rausfiltern."

          Das steht ÜBER den Kacheln und nicht darunter: die Zeilen fehlen in
          jeder Zahl dahinter, und wer das nicht weiss, hält eine Datei mit
          sechzig Zeilen für eine mit zwanzig. Je Ort und mit Zahl - steht hier
          ein Ort, den niemand ignorieren wollte, sieht man es sofort. */}
      {(vorschau?.ignorierte_orte ?? []).length > 0 && (
        <div className="hinweis" style={{ marginTop: 14 }}>
          <b>Aus Orten, in denen wir nicht arbeiten, wurde nichts
          eingelesen.</b>{' '}
          {vorschau.ignorierte_orte
            .map((o: any) => `${o.ort} (${o.zeilen})`).join(', ')}.
          {' '}Diese Zeilen stehen in keiner Zahl unten. Wer das ändern will,
          gibt den Ort unter Konfiguration → Betrieb → „Orte, in denen wir
          nicht arbeiten" wieder frei.
        </div>
      )}

      {/* UND DIE ORTE, DIE DIESES HAUS NOCH NIE GESEHEN HAT.
          ORIGINALTEXT MARVIN (08.09.2026)
            „jetzt habe ich hier wieder aufträge drin, die durch den filter
             dauerhaft ausgeblendet sein sollen. kümmer dich endlich final
             darum!!"
          Der Kasten daneben zählt, was die Sperrliste ABGEWIESEN hat; dieser
          zählt, was sie gar nicht kennen konnte. **Ein Ort, den niemand kennt,
          steht in keiner Sperrliste** - und bis .139 gab es nicht einmal eine
          Zeile, an der der Haken hätte hängen können. Sie entsteht jetzt beim
          Anwenden, und dieser Satz sagt vorher, um welche Orte es geht. */}
      {(vorschau?.neue_orte ?? []).length > 0 && (
        <div className="hinweis warnung" style={{ marginTop: 14 }}>
          <b>In diesen Orten hat dieses System noch nie einen Auftrag
          gehabt.</b>{' '}
          {vorschau.neue_orte
            .map((o: any) => `${o.ort} (${o.zeilen})`).join(', ')}.
          {' '}Sie werden beim Anwenden angelegt und die Aufträge entstehen -
          es wird nichts geraten. Gehören sie nicht zu uns, sperrt sie
          Konfiguration → Betrieb → „Orte, in denen wir nicht arbeiten"
          dauerhaft; der nächste Abruf bringt sie dann nicht mehr herein.
        </div>
      )}

      {/* ---------------------------------------------------- Was passiert */}
      {plan && hatTelekom && (
        <>
          <div className="kachelreihe" style={{ marginTop: 14 }}>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--laufend)' }}>
                {plan.zahlen.neu}
              </div>
              <div className="was">werden angelegt</div>
            </div>
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--neu)' }}>
                {plan.zahlen.geaendert}
              </div>
              <div className="was">werden geändert</div>
            </div>
            <div className="kachel">
              <div className="wert">{plan.zahlen.unveraendert}</div>
              <div className="was">bleiben, wie sie sind</div>
            </div>
            <div className="kachel">
              <div className="wert" style={{
                color: plan.zahlen.fehlen ? 'var(--ueberfaellig)' : undefined }}>
                {plan.zahlen.fehlen}
              </div>
              <div className="was">stehen nicht in der Datei</div>
            </div>
          </div>

          {/* DIE DREI ERSTEN KACHELN DECKEN JEDE GELESENE ZEILE AB, und bis
              zum 27.08.2026 taten sie das nicht: eine stornierte Zeile hiess
              „storniert" statt „geändert" und tauchte in keiner Kachel auf.
              Bei Marvins Datei standen 341 Zeilen darin und 310 auf dem
              Bildschirm. Storno und Archiv sind EIGENSCHAFTEN und stehen
              deshalb als Satz daneben, nicht als vierte Kachel: sonst zählt
              jemand sie dazu und kommt auf mehr Zeilen, als die Datei hat. */}
          {(plan.zahlen.storniert > 0 || plan.zahlen.archiviert > 0) && (
            <p className="klein" style={{ marginTop: 8 }}>
              {plan.zahlen.storniert > 0 && (
                <>Davon <b>{plan.zahlen.storniert}</b>{' '}
                {plan.zahlen.storniert === 1 ? 'von der Telekom storniert' : 'von der Telekom storniert'}
                {' '}&ndash; die Stammdaten werden nachgezogen, über den
                Zustand entscheidet der Innendienst.{' '}</>
              )}
              {plan.zahlen.archiviert > 0 && (
                <><b>{plan.zahlen.archiviert}</b> archiviert und deshalb in
                keinem Feld angefasst.</>
              )}
            </p>
          )}

          {/* WORAN DIE AUFTRÄGE WIEDERERKANNT WURDEN.

              ORIGINALTEXT MARVIN (28.08.2026)
                „ja, wir gehen immer bei jedem auftrag über die BAFA-ID denn
                 diese ist immer einduetig, egal ob die SM oder KLS da ist
                 oder nicht."

              Ein Satz und keine Kachel: es ist keine vierte Sorte Zeile,
              sondern eine Auskunft über die drei darüber. Er steht hier, weil
              „über die Anschrift" der Zustand VOR der Umstellung ist - solange
              die Zahl gross ist, hängen diese Aufträge noch an ihrer
              Schreibweise, und ein Buchstabe in der Strasse macht aus einem
              zwei. Die Zahl fällt mit jedem Lauf. */}
          {(plan.zahlen.ueber_kennung > 0 || plan.zahlen.ueber_anschrift > 0) && (
            <p className="klein leise" style={{ marginTop: 6 }}>
              Wiedererkannt: <b>{plan.zahlen.ueber_kennung}</b> über die
              Kennung (BA/FA-ID oder Bauauftrag-ID)
              {plan.zahlen.ueber_anschrift > 0 && (
                <>, <b>{plan.zahlen.ueber_anschrift}</b> nur über die Anschrift
                {' '}&ndash; diese bekommen ihre Kennung mit diesem Lauf</>
              )}.
            </p>
          )}

          {/* HABEN DIE ZWEI DATEIEN ZUSAMMENGEFUNDEN?

              ORIGINALTEXT MARVIN (08.09.2026)
                „hast du jetzt in den zwei dokumenten order und bafa ein match
                 gefunden um die daten zusammenzulegen in unserer
                 auftragsakte?"

              **DAS IST DIE FRAGE, DIE MAN BEIM HOCHLADEN ZWEIER DATEIEN HAT**,
              und die Antwort stand bisher in keinem Bildschirm: der Plan trägt
              sie seit dem 28.08.2026 je Zeile, gelesen hat sie nur der Satz,
              der beim Anlegen in die Auftragsinfo geht.

              Der Satz steht NUR da, wenn wirklich beide Dateien im Spiel sind.
              Bei einer einzelnen Datei wäre „0 aus beiden" richtig gerechnet
              und trotzdem eine Auskunft über etwas, das niemand gefragt hat -
              und eine Zeile, die immer dasteht, wird nicht mehr gelesen.

              GEZÄHLT WIRD AUF DEM SERVER (`plan.zahlen`). Die Oberfläche hat
              die Zeilen und könnte es selbst; dann stünde dieselbe Rechnung
              zweimal da. */}
          {plan.zahlen.nur_abruf > 0 && plan.zahlen.nur_bafa > 0 && (
            <p className="klein leise" style={{ marginTop: 6 }}>
              Aus beiden Dateien zusammengeführt:{' '}
              <b>{plan.zahlen.aus_beiden}</b>
              {' '}über die Anschrift. Nur im Abruf:{' '}
              <b>{plan.zahlen.nur_abruf}</b>, nur in der BAFA:{' '}
              <b>{plan.zahlen.nur_bafa}</b>.
              {plan.zahlen.aus_beiden === 0 && (
                <> Keine einzige Anschrift steht in beiden Dateien &ndash; das
                  ist bei 2 Dateien desselben Gebiets ungewöhnlich und meist
                  ein Zeichen dafür, dass sie aus verschiedenen Abrufen
                  stammen.</>
              )}
            </p>
          )}

          {/* WELCHES FELD SICH ÄNDERT, NICHT NUR WIE VIELE ZEILEN.

              ORIGINALTEXT MARVIN (26.08.2026)
                „das wundert mich ein wenig das er so viele ändern will aber
                 okay."

              Das „aber okay" ist der Satz, der teuer wird. Vier Kacheln mit
              Zahlen darauf beantworten die einzige Frage nicht, die vor dem
              Klicken zählt: WAS ändert sich denn. Solange das nur eine Zahl
              war, blieb Marvin nichts übrig, als sie zu glauben. */}
          {plan.felder.length > 0 && (
            <>
              {/* DIE BEDINGUNG STEHT NICHT MEHR HIER, sondern in
                  `regeln/import.ts` als `plan.verdacht`. Sie entscheidet seit
                  dem 27.08.2026 auch, ob der selbsttätige Lauf ANHÄLT - und
                  zwei Abschriften derselben Regel laufen beim ersten Nachtrag
                  auseinander. Dann würde der Kasten warnen, während der Lauf
                  schreibt, oder umgekehrt. */}
              {plan.verdacht && (
                <div className="hinweis warnung" style={{ marginTop: 12 }}>
                  <b>Kein einziger Auftrag bleibt, wie er ist</b>
                  {plan.verdacht} Erst ansehen, dann anwenden.
                </div>
              )}
              <p className="klein" style={{ marginTop: 12, marginBottom: 6 }}>
                <b>Was sich ändert.</b> Je Feld gezählt, häufigstes zuerst, mit
                einem Beispiel aus der Datei.
              </p>
              <table className="liste">
                <thead>
                  <tr><th>Feld</th>
                      <th className="zahl" style={{ width: 90 }}>Aufträge</th>
                      <th style={{ width: 220 }}>steht im System</th>
                      <th style={{ width: 220 }}>steht in der Datei</th></tr>
                </thead>
                <tbody>
                  {plan.felder.map((f: any) => (
                    <tr key={f.feld} style={{ cursor: 'default' }}>
                      <td className="klein">
                        {TELEKOM_TEXT[f.feld] ?? f.feld}
                      </td>
                      {/* ROT WIRD DAS FELD, DAS DEN VERDACHT TRÄGT: eines,
                          das an JEDER geänderten Zeile anders ist. Ob es
                          überhaupt einen Verdacht gibt, entscheidet der Plan
                          (`verdacht`) und nicht diese Zelle - hier stand
                          dieselbe Bedingung ein zweites Mal abgeschrieben. */}
                      <td className="zahl" style={{
                        color: plan.verdacht && f.anzahl === plan.zahlen.geaendert
                                 ? 'var(--ueberfaellig)' : undefined }}>
                        {f.anzahl}
                      </td>
                      <td className="klein">{f.alt || <i className="leise">leer</i>}</td>
                      <td className="klein leise">
                        {f.neu || <i>leer</i>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {vorschau.doppelteArt.length > 0 && (
            <div className="hinweis" style={{ marginTop: 12 }}>
              <b>Mehrere Dateien derselben Art</b>
              {vorschau.doppelteArt.map((a: string) => ART_TEXT[a]).join(', ')}.
              Sie werden zusammengelegt.
            </div>
          )}

          {plan.doppelt.length > 0 && (
            <div className="hinweis warnung" style={{ marginTop: 10 }}>
              <b>
                {plan.doppelt.length === 1
                  ? 'Eine Anschrift steht mehrfach in derselben Datei'
                  : `${plan.doppelt.length} Anschriften stehen mehrfach in derselben Datei`}
              </b>
              {/* SEIT DEM 28.08.2026 STIMMT „ÄNDERN NICHTS" NICHT MEHR - und
                  das ist der Punkt der Umstellung. Ein Mehrfamilienhaus steht
                  mit vier Wohnungsanschlüssen im Abruf; die Felder nimmt
                  weiterhin die erste Zeile, aber die Bauauftrag-IDen aller
                  vier werden festgehalten. Vorher fielen drei davon
                  ersatzlos heraus. */}
              Die Felder nimmt die erste Zeile. Die Kennungen der weiteren
              werden am selben Auftrag festgehalten &ndash; damit die Datei
              beim nächsten Mal jede von ihnen wiederfindet.
              <div className="klein" style={{ marginTop: 5 }}>
                {plan.doppelt.slice(0, 5).map((x: any) => x.schluessel).join(', ')}
                {plan.doppelt.length > 5 && ` und ${plan.doppelt.length - 5} weitere`}
              </div>
            </div>
          )}

          {/* EINE KENNUNG, DIE SCHON WOANDERS HÄNGT. Sie wird nicht umgehängt:
              eine Kennung, die wandert, nimmt die halbe Akte mit - Termine,
              Aufmaße, Rechnungen. Deshalb steht sie hier und nicht in einer
              Protokolldatei. */}
          {plan.konflikte?.length > 0 && (
            <div className="hinweis warnung" style={{ marginTop: 10 }}>
              <b>
                {plan.konflikte.length === 1
                  ? 'Eine Kennung steht in der Datei an einer anderen Anschrift'
                  : `${plan.konflikte.length} Kennungen stehen in der Datei an anderen Anschriften`}
              </b>
              Sie werden nicht umgehängt. Die Felder der Zeile werden an der
              Anschrift geschrieben, an der sie ankommt.
              <table className="liste" style={{ marginTop: 6 }}>
                <thead>
                  <tr><th>Kennung</th><th>in der Datei</th><th>im System</th></tr>
                </thead>
                <tbody>
                  {plan.konflikte.slice(0, 10).map((k: any, i: number) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                      <td className="klein">
                        {k.art === 'bafa' ? 'BA/FA-ID' : 'Bauauftrag-ID'} {k.wert}
                      </td>
                      <td className="klein">{k.anschrift}</td>
                      <td className="klein leise">{k.anderer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {plan.konflikte.length > 10 && (
                <div className="klein leise" style={{ marginTop: 4 }}>
                  und {plan.konflikte.length - 10} weitere
                </div>
              )}
            </div>
          )}

          {plan.verworfen.map((v: any, i: number) => (
            <div key={i} className="hinweis warnung" style={{ marginTop: 10 }}>
              <b>
                {v.anzahl === 1 ? 'Eine Zeile wurde nicht gelesen'
                                : `${v.anzahl} Zeilen wurden nicht gelesen`}
              </b>{v.grund}
            </div>
          ))}

          {plan.fehlen.length > 0 && (
            <div className="hinweis" style={{ marginTop: 10 }}>
              <b>
                {plan.fehlen.length === 1
                  ? 'Ein Auftrag steht hier, aber nicht in der Datei'
                  : `${plan.fehlen.length} Aufträge stehen hier, aber nicht in der Datei`}
              </b>
              Sie werden NICHT angefasst und nicht archiviert: eine Datei kann
              ein Ausschnitt sein.
            </div>
          )}

        </>
      )}

      {/* ------------------------------------------------ Die Kundenliste */}
      {kunden && <Kundenteil k={kunden} hinweis={vorschau.kundenHinweis} />}

      {/* Der Knopf steht AUSSERHALB beider Blöcke: eine reine Kundenliste hat
          keinen Abrufplan, und ohne diesen Schritt gäbe es für sie gar keinen
          Knopf zum Anwenden. */}
      {vorschau && !ergebnis && (
        <>
          <div className="reihe" style={{ marginTop: 14 }}>
            <button className="knopf haupt" disabled={laeuft || !darfAnwenden}
                    onClick={anwenden}
                    title={darfAnwenden ? undefined
                      : 'Dafür fehlt das Recht „import.anwenden".'}>
              {laeuft ? 'Einen Moment'
                : hatTelekom
                  ? `${plan.zahlen.neu} anlegen, ${plan.zahlen.geaendert} ändern`
                  : `${(kunden?.fuellungen ?? []).reduce(
                      (n: number, f: any) => n + f.felder.length, 0)} Felder füllen`}
            </button>
            <button className="knopf" onClick={zu} disabled={laeuft}>
              Abbrechen
            </button>
          </div>
          {!darfAnwenden && (
            <p className="klein leise" style={{ marginTop: 6 }}>
              Ansehen darfst du das, schreiben nicht. Das Recht heißt
              „import.anwenden" und hängt an einer Gruppe.
            </p>
          )}
        </>
      )}

      {/* -------------------------------------------------------- Was war */}
      {ergebnis && (
        <>
          <div className="hinweis" style={{ marginTop: 12 }}>
            <b>Eingelesen</b>{ergebnis.klartext}
          </div>
          <p className="klein leise">
            Die Datei ist damit im Posteingang erledigt.
          </p>
          {ergebnis.kunden && <Kundenteil k={ergebnis.kunden} getan />}
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt" onClick={fertig}>Fertig</button>
          </div>
        </>
      )}
    </Dialog>
  );
}


/**
 * Was eine hochgeladene Kundenliste bewirken würde.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „sind die manuell hochgeladenen kundendaten zu den aufträgen verbunden und
 *    sychronisiert? kann ich listen und kundendaten hochladen?"
 *
 * DREI ZAHLEN, UND DIE MITTLERE IST DIE WICHTIGE. Was gefüllt wird, ist der
 * Ertrag. Was ABWEICHT, ist die Arbeit: dort steht schon etwas anderes, und
 * es wird NICHT überschrieben. Eine Liste, die vierhundert Telefonnummern
 * still durch ältere ersetzt, merkt niemand - deshalb steht hier jede
 * Abweichung einzeln, mit beiden Werten nebeneinander.
 */
function Kundenteil({ k, hinweis, getan }:
                    { k: any; hinweis?: string | null; getan?: boolean }) {
  // In der VORSCHAU steht die Zahl in den geplanten Füllungen, im ERGEBNIS
  // liefert der Server sie fertig. Zwei Quellen, eine Anzeige: die Zahl im
  // Ergebnis ist das, was WIRKLICH geschrieben wurde, und die darf hier nicht
  // aus dem Plan nachgerechnet werden.
  const felder = getan
    ? (k.gefuellt ?? 0)
    : (k.fuellungen ?? []).reduce((n: number, f: any) => n + f.felder.length, 0);
  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <h3>Kundendaten aus der Liste</h3>
      <div className="leib">
        {hinweis && <div className="hinweis">{hinweis}</div>}
        <div className="kachelreihe">
          <div className="kachel">
            <div className="wert" style={{ color: 'var(--laufend)' }}>{felder}</div>
            <div className="was">
              {getan ? 'Felder gefüllt' : 'Felder werden gefüllt'}
            </div>
          </div>
          <div className="kachel">
            <div className="wert" style={{
              color: k.abweichungen.length ? 'var(--neu)' : undefined }}>
              {k.abweichungen.length}
            </div>
            <div className="was">weichen ab</div>
          </div>
          <div className="kachel">
            <div className="wert">{k.gleich}</div>
            <div className="was">stehen schon so da</div>
          </div>
          <div className="kachel">
            <div className="wert" style={{
              color: k.verworfen.length ? 'var(--ueberfaellig)' : undefined }}>
              {k.verworfen.length}
            </div>
            <div className="was">ohne Auftrag</div>
          </div>
        </div>

        {k.abweichungen.length > 0 && (
          <>
            <p className="klein" style={{ marginTop: 12, marginBottom: 6 }}>
              <b>Hier steht schon etwas anderes.</b> Es wird NICHT
              überschrieben: der erfasste Wert stammt oft vom Kunden selbst und
              ist neuer als die Liste. Wer die Liste für richtiger hält, trägt
              den Wert im Auftrag von Hand ein.
            </p>
            <table className="liste">
              <thead>
                <tr><th>Auftrag</th>
                    <th style={{ width: 120 }}>Feld</th>
                    <th style={{ width: 200 }}>steht im System</th>
                    <th style={{ width: 200 }}>steht in der Liste</th></tr>
              </thead>
              <tbody>
                {k.abweichungen.slice(0, 40).map((a: any, i: number) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td className="klein">
                      {a.anschrift}
                      {a.klsId && <div className="leise">KLS {a.klsId}</div>}
                    </td>
                    <td className="klein">{FELD_TEXT[a.feld] ?? a.feld}</td>
                    <td className="klein">{a.steht}</td>
                    <td className="klein leise">{a.liste}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {k.abweichungen.length > 40 && (
              <p className="klein leise">
                … und {k.abweichungen.length - 40} weitere. Alle stehen nach
                dem Anwenden im Laufprotokoll.
              </p>
            )}
          </>
        )}

        {k.verworfen.length > 0 && (
          <div className="hinweis warnung" style={{ marginTop: 10 }}>
            <b>
              {k.verworfen.length === 1
                ? 'Eine Zeile ließ sich keinem Auftrag zuordnen'
                : `${k.verworfen.length} Zeilen ließen sich keinem Auftrag zuordnen`}
            </b>
            <div className="klein" style={{ marginTop: 5 }}>
              {k.verworfen.slice(0, 6).map((v: any, i: number) => (
                <div key={i}>Zeile {v.zeile}: {v.was} - {v.grund}</div>
              ))}
              {k.verworfen.length > 6
                && <div>… und {k.verworfen.length - 6} weitere.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
